import { memo, useState, useCallback, useMemo } from "react";
import MathText from '../MathText';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import { etatsOrdonner } from './corrige';

/**
 * Composant interactif "Ordonner" côté élève — style Duolingo.
 * Slots numérotés en haut (zone réponse), chips mélangées en bas.
 * Tap chip → va dans le prochain slot. Tap slot → retourne dans les disponibles.
 *
 * @param {Array} items - ["1/4", "1/3", "1/2"] (format contenu.items, déjà dans le bon ordre)
 * @param {string} selectedValue - Réponse existante (JSON stringifié) pour restauration
 * @param {Function} onAnswer - Callback (JSON stringifié du tableau ordonné)
 * @param {Object|null} corrige - Correction sur place (lot 5, 2026-09-21), voir plus bas
 */
const OrdonnerEleve = memo(function OrdonnerEleve({ items: correctItems, selectedValue, onAnswer, onComplete, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);

    // ─── Correction sur place (lot 5, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Ses cases restent dans son ordre, chacune verte ou rouge selon que
    // l'élément est au bon rang ; dessous, la suite attendue réécrite en
    // entier en pointillé (« La réponse »), parce qu'une case juste ne l'est
    // que par rapport à ses voisines. Le bac disparaît, tout se fige. Sans
    // `corrige`, le rendu d'avant.
    const etats = useMemo(() => (corrige ? etatsOrdonner(corrige) : null), [corrige]);
    // items arrive déjà dans le bon ordre depuis contenu — on les mélange pour l'élève
    const sortedItems = useMemo(() => {
        if (!correctItems || correctItems.length === 0) return [];
        return [...correctItems];
    }, [correctItems]);

    // Mélanger les éléments une seule fois
    const shuffledItems = useMemo(() => {
        const shuffled = [...sortedItems];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [sortedItems]);

    // State : items placés dans les slots + items encore disponibles
    const [placed, setPlaced] = useState(() => {
        // Restaurer une réponse existante
        if (selectedValue) {
            try {
                const parsed = JSON.parse(selectedValue);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch { /* ignore */ }
        }
        return [];
    });

    const available = useMemo(() => {
        // Items mélangés qui n'ont pas encore été placés — gérer les doublons un par un
        const remaining = [...shuffledItems];
        for (const p of placed) {
            const idx = remaining.indexOf(p);
            if (idx !== -1) remaining.splice(idx, 1);
        }
        return remaining;
    }, [shuffledItems, placed]);

    // Pas de resync sur selectedValue : l'état est lu UNE FOIS au montage,
    // le montage est keyé par question (Quiz / ExerciseurPreview).

    // Tap chip disponible → placer dans le prochain slot
    const handlePlaceItem = useCallback((item) => {
        // La liste suivante se calcule ICI, et c'est la même valeur qu'on donne
        // à React et au parent. Jamais dans la fonction passée à setState :
        // React peut la rejouer, et un rejeu qui prévient le parent fait
        // boucler (même piège que l'horloge, corrigé le 2026-09-21).
        const next = [...placed, item];
        const isComplete = next.length === sortedItems.length;
        setPlaced(next);
        if (isComplete) onAnswer(JSON.stringify(next));
        onComplete(isComplete);
    }, [placed, sortedItems.length, onAnswer, onComplete]);

    // Tap slot rempli → retirer et remettre dans les disponibles
    const handleRemoveItem = useCallback((slotIndex) => {
        setPlaced(prev => prev.filter((_, i) => i !== slotIndex));
        onComplete(false);
    }, [onComplete]);

    if (sortedItems.length === 0) return null;

    return (
        <>
            {/* Pas de bandeau d'instruction : les slots numérotés + étiquettes
                cliquables se découvrent au premier clic (épure 2026-07-31). */}
            {/* Zone réponse — slots numérotés */}
            <div className={`flex flex-wrap gap-2 px-4 py-3 rounded-xl border-2 border-dashed ${t.dropzone} min-h-[56px] mt-3 sm:mt-4`}>
                {etats ? etats.places.map((p, i) => {
                    const etat = p.juste ? 'juste' : 'faux';
                    return (
                        <button
                            key={i}
                            type="button"
                            disabled
                            data-etat={etat}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-medium cursor-default ${t[etat]}`}
                        >
                            <span className="text-[10px] font-bold opacity-70">{i + 1}.</span>
                            <MathText>{p.item}</MathText>
                            <span aria-hidden="true" className="font-black">{marqueSigne(etat)}</span>
                        </button>
                    );
                }) : sortedItems.map((_, i) => {
                    const placedItem = placed[i];
                    return placedItem ? (
                        // Slot rempli — cliquable pour retirer
                        <button
                            key={i}
                            type="button"
                            onClick={() => handleRemoveItem(i)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-medium transition-all ${t.selected} ${t.removeHover}`}
                        >
                            <span className={`text-[10px] font-bold ${clair ? 'text-blue-500' : 'text-blue-400'}`}>{i + 1}.</span>
                            <MathText>{placedItem}</MathText>
                        </button>
                    ) : (
                        // Slot vide
                        <div
                            key={i}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-dashed ${t.slotEmptyBorder} min-w-[60px]`}
                        >
                            <span className={`text-[10px] font-bold ${t.slotEmptyText}`}>{i + 1}.</span>
                            <span className={`text-sm ${t.slotEmptyText}`}>—</span>
                        </div>
                    );
                })}
            </div>

            {/* Correction : la suite attendue en entier, sous la sienne */}
            {etats ? (
                <div className="mt-3" data-testid="ordre-attendu">
                    <span className="block mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">La réponse</span>
                    <div className="flex flex-wrap gap-2">
                        {etats.attendus.map((item, i) => (
                            <span key={i} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-bold ${t.attendu}`}>
                                <span className="text-[10px] font-bold opacity-70">{i + 1}.</span>
                                <MathText>{item}</MathText>
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Chips disponibles (jamais en correction) */}
            {!etats && available.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mt-3">
                    {available.map((item, i) => (
                        <button
                            key={`${item}-${i}`}
                            type="button"
                            onClick={() => handlePlaceItem(item, i)}
                            className={`px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-medium cursor-pointer hover:scale-105 transition-all shadow-sm ${t.chip}`}
                        >
                            <MathText>{item}</MathText>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
});

export default OrdonnerEleve;
